
export const PARENT_PORTFOLIO_KEYS = {
  MAIN: "main-portfolio",
};

export const MAIN_PORTFOLIO_COMPOSITION_TITLE = "Portfolio Main";

export const MAIN_PORTFOLIO_COMPOSITION_CHART_KEYS = [
  "Portfolio Main",
  "Main Portfolio composition",
  "Main portfolio chart",
  "Composición del portafolio principal",
  "Gráfica del portafolio main",
  "Gráfica del portafolio principal",
];

export const PARENT_PORTFOLIO_API_IDS = {
  [PARENT_PORTFOLIO_KEYS.MAIN]: "DSPFOLIO",
};

const PARENT_META = {
  [PARENT_PORTFOLIO_KEYS.MAIN]: {
    apiId: PARENT_PORTFOLIO_API_IDS[PARENT_PORTFOLIO_KEYS.MAIN],
    displayName: "Main Portfolio",
    hasSubportfolios: true,
    callsApi: false,
  },
};

const CANONICAL_PARENT_KEYS = new Set(Object.values(PARENT_PORTFOLIO_KEYS));

export const PORTFOLIO_IDS = {
  DS_PRIVATE_CREDIT: 1,
  DS_PRIVATE_EQUITY: 2,
  DS_INTERNATIONAL_EQUITY: 3,
  DS_FIXED_INCOME: 4,
  DS_REAL_ESTATE: 5,
  DS_DOMESTIC_EQUITY: 6,
};

export const PORTFOLIO_KEYS = {
  DS_PRIVATE_CREDIT: "ds-private-credit",
  DS_PRIVATE_EQUITY: "ds-private-equity",
  DS_INTERNATIONAL_EQUITY: "ds-international-equity",
  DS_FIXED_INCOME: "ds-fixed-income",
  DS_REAL_ESTATE: "ds-real-estate",
  DS_DOMESTIC_EQUITY: "ds-domestic-equity",
};

const CANONICAL_KEYS = new Set(Object.values(PORTFOLIO_KEYS));

const KEY_TO_ID = {
  [PORTFOLIO_KEYS.DS_PRIVATE_CREDIT]: PORTFOLIO_IDS.DS_PRIVATE_CREDIT,
  [PORTFOLIO_KEYS.DS_PRIVATE_EQUITY]: PORTFOLIO_IDS.DS_PRIVATE_EQUITY,
  [PORTFOLIO_KEYS.DS_INTERNATIONAL_EQUITY]: PORTFOLIO_IDS.DS_INTERNATIONAL_EQUITY,
  [PORTFOLIO_KEYS.DS_FIXED_INCOME]: PORTFOLIO_IDS.DS_FIXED_INCOME,
  [PORTFOLIO_KEYS.DS_REAL_ESTATE]: PORTFOLIO_IDS.DS_REAL_ESTATE,
  [PORTFOLIO_KEYS.DS_DOMESTIC_EQUITY]: PORTFOLIO_IDS.DS_DOMESTIC_EQUITY,
};

const ORDERED_IDS = [
  PORTFOLIO_IDS.DS_PRIVATE_CREDIT,
  PORTFOLIO_IDS.DS_PRIVATE_EQUITY,
  PORTFOLIO_IDS.DS_INTERNATIONAL_EQUITY,
  PORTFOLIO_IDS.DS_FIXED_INCOME,
  PORTFOLIO_IDS.DS_REAL_ESTATE,
  PORTFOLIO_IDS.DS_DOMESTIC_EQUITY,
];

const META = {
  [PORTFOLIO_IDS.DS_PRIVATE_CREDIT]: {
    key: "ds-private-credit",
    shortLabel: "Private Credit",
    displayName: "DS Private Credit",
    apiId: "DSPRIVCRE",
  },
  [PORTFOLIO_IDS.DS_PRIVATE_EQUITY]: {
    key: "ds-private-equity",
    shortLabel: "Private Equity",
    displayName: "DS Private Equity",
    apiId: "DSPRIVEQ",
  },
  [PORTFOLIO_IDS.DS_INTERNATIONAL_EQUITY]: {
    key: "ds-international-equity",
    shortLabel: "International Equity",
    displayName: "DS International Equity",
    apiId: "DSINTEQ",
  },
  [PORTFOLIO_IDS.DS_FIXED_INCOME]: {
    key: "ds-fixed-income",
    shortLabel: "Fixed Income",
    displayName: "DS Fixed Income",
    apiId: "DSFIXINC",
  },
  [PORTFOLIO_IDS.DS_REAL_ESTATE]: {
    key: "ds-real-estate",
    shortLabel: "Real Estate",
    displayName: "DS Real Estate",
    apiId: "DSREALEST",
  },
  [PORTFOLIO_IDS.DS_DOMESTIC_EQUITY]: {
    key: "ds-domestic-equity",
    shortLabel: "Domestic Equity",
    displayName: "DS Domestic Equity",
    apiId: "DSDOMEQ",
  },
};

export function isValidPortfolioId(id) {
  return Number.isInteger(id) && ORDERED_IDS.includes(id);
}

export function normalizePortfolioKey(raw) {
  if (raw == null || typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase().replace(/_/g, "-");
  if (CANONICAL_KEYS.has(k)) return k;
  return null;
}

export function portfolioKeyToId(portfolioKey) {
  const k = normalizePortfolioKey(portfolioKey);
  if (!k) return null;
  return KEY_TO_ID[k] ?? null;
}

export function resolvePortfolioIdFromSelectToolArgs(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.portfolioKey != null && parsed.portfolioKey !== "") {
    const fromKey = portfolioKeyToId(String(parsed.portfolioKey));
    if (fromKey != null) return fromKey;
  }
  const n = Number(parsed.portfolioId);
  if (isValidPortfolioId(n)) return n;
  return null;
}

export function getPortfolioMeta(portfolioId) {
  return META[portfolioId] ?? null;
}

export function isValidParentKey(parentKey) {
  return typeof parentKey === "string" && CANONICAL_PARENT_KEYS.has(parentKey);
}

export function normalizeParentKey(raw) {
  if (raw == null || typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase().replace(/_/g, "-");
  if (CANONICAL_PARENT_KEYS.has(k)) return k;
  return null;
}

export function getParentMeta(parentKey) {
  if (!isValidParentKey(parentKey)) return null;
  return PARENT_META[parentKey];
}

export function getParentApiId(parentKey) {
  return getParentMeta(parentKey)?.apiId ?? null;
}

export function getParentDisplayName(parentKey) {
  return getParentMeta(parentKey)?.displayName ?? null;
}

export function parentHasSubportfolios(parentKey) {
  return getParentMeta(parentKey)?.hasSubportfolios === true;
}

export function resolveParentKeyFromSelectToolArgs(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  return normalizeParentKey(parsed.parentKey);
}

export function getContentPagePortfolioHeadline({
  parentKey = null,
  subPortfolioId = null,
} = {}) {
  if (
    isValidParentKey(parentKey) &&
    parentHasSubportfolios(parentKey) &&
    !isValidPortfolioId(subPortfolioId)
  ) {
    return MAIN_PORTFOLIO_COMPOSITION_TITLE;
  }
  if (isValidPortfolioId(subPortfolioId)) {
    const meta = META[subPortfolioId];
    return meta ? `Sub-Portfolio - ${meta.displayName}` : null;
  }
  return null;
}
